import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import api from '../utils/api';

// Always use KnowledgeBase (migration complete)
const USE_KNOWLEDGE_BASE = true;

const ChatInterface = ({ companyTicker, companyName, dateRange, onBackToInput }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessStep, setReprocessStep] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      content: inputMessage,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError('');

    try {
      let response;
      
      if (USE_KNOWLEDGE_BASE) {
        // Use new KnowledgeBase RAG endpoint
        console.log('🔍 Sending KnowledgeBase RAG request with:', {
          company_id: companyTicker, // Using ticker as company_id for now
          ticker: companyTicker,
          company_name: companyName,
          query: inputMessage
        });
        
        response = await api.post('/api/knowledge-base/rag/chat', {
          company_id: companyTicker,
          ticker: companyTicker,
          company_name: companyName,
          query: inputMessage
        });

        console.log('🔍 KnowledgeBase RAG response:', response.data);
        
        const assistantMessage = {
          id: Date.now() + 1,
          content: response.data.response,
          role: 'assistant',
          timestamp: new Date(),
          sources: response.data.citations?.map(citation => ({
            filingType: citation.filing_type,
            filingDate: citation.filing_date,
            accessionNumber: citation.accession_number,
            confidence: citation.score,
            type: 'text_chunk', // KnowledgeBase citations are text chunks
            url: `https://www.sec.gov/Archives/edgar/data/${citation.accession_number.split('-')[0]}/${citation.accession_number.replace(/-/g, '')}/${citation.accession_number}.txt`
          })) || [],
          metadata: {
            numSources: response.data.num_sources,
            query: response.data.query
          }
        };
        
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Use KnowledgeBase RAG endpoint
        console.log('🔍 Sending KnowledgeBase RAG request with:', {
          company_id: companyTicker,
          ticker: companyTicker,
          company_name: companyName,
          query: inputMessage
        });
        
        response = await api.post('/api/knowledge-base/rag/chat', {
          company_id: companyTicker,
          ticker: companyTicker,
          company_name: companyName,
          query: inputMessage
        });

        console.log('🔍 KnowledgeBase RAG response:', response.data);
        
        const assistantMessage = {
          id: Date.now() + 1,
          content: response.data.response,
          role: 'assistant',
          timestamp: new Date(),
          sources: response.data.citations || [],
          metadata: {
            num_sources: response.data.num_sources,
            query: response.data.query
          }
        };
        
        setMessages(prev => [...prev, assistantMessage]);
      }
      
    } catch (err) {
      console.error('🔍 Error sending message:', err);
      console.error('🔍 Error response:', err.response);
      console.error('🔍 Error data:', err.response?.data);
      setError(err.response?.data?.message || err.message || 'Failed to send message');
      
      const errorMessage = {
        id: Date.now() + 1,
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const reprocessTicker = async () => {
    try {
      setIsReprocessing(true);
      setError('');
      setReprocessStep('Starting reprocessing...');

      if (USE_KNOWLEDGE_BASE) {
        // Use KnowledgeBase reprocessing
        setReprocessStep('Resolving ticker and processing filings...');
        
        // Resolve ticker
        await api.get(`/api/knowledge-base/lookup?ticker=${companyTicker}`);
         
        // Process company filings
        const queryParams = new URLSearchParams({
          ticker: companyTicker,
          startDate: dateRange?.start || '2019-01-01',
          endDate: dateRange?.end || new Date().toISOString().split('T')[0]
        });
        
        const processResponse = await api.post(`/api/knowledge-base/process-company?${queryParams.toString()}`, {});
        
        if (!processResponse.data.success) {
          throw new Error(processResponse.data.message || 'Reprocessing failed');
        }
        
        const processingId = processResponse.data.processingId;
        const syncJobId = processResponse.data.syncJobId;
        
        if (!processingId) {
          setReprocessStep('No new filings to process. Ready for questions!');
          setIsReprocessing(false);
          return;
        }
        
        // Poll status until completion
        setReprocessStep('Processing filings...');
        const startTime = Date.now();
        const maxMs = 30 * 60 * 1000; // 30 minutes
        
        while (Date.now() - startTime < maxMs) {
          const statusRes = await api.get(`/api/knowledge-base/status/${processingId}`, { timeout: 120000 });
          const status = statusRes.data;
          
          if (status.status === 'completed') {
            if (syncJobId) {
              // Check KB sync status
              const syncStatusRes = await api.get(`/api/knowledge-base/sync-status/${syncJobId}`);
              const syncStatus = syncStatusRes.data;
              
              if (syncStatus.status === 'COMPLETED') {
                setReprocessStep('Completed! You can ask questions now.');
                setIsReprocessing(false);
                return;
              } else if (syncStatus.status === 'FAILED') {
                throw new Error(`KB sync failed: ${syncStatus.failure_reason}`);
              } else {
                setReprocessStep(`KB Sync: ${syncStatus.status}`);
                await new Promise(r => setTimeout(r, 2000));
                continue;
              }
            } else {
              setReprocessStep('Completed! You can ask questions now.');
              setIsReprocessing(false);
              return;
            }
          }
          
          if (status.status === 'failed') {
            throw new Error(status.errors?.join(', ') || 'Processing failed');
          }
          
          setReprocessStep(`${status.progress.step} (${status.progress.current}/${status.progress.total})`);
          await new Promise(r => setTimeout(r, 2000));
        }
        
        throw new Error('Reprocessing timed out after 30 minutes');
      } else {
        // Original reprocessing logic
        setReprocessStep('Resolving ticker and downloading filings...');

        // 1) Resolve ticker (optional but useful to validate)
        await api.get(`/api/sec/lookup?ticker=${companyTicker}`);

        // 2) Download latest filings
        const queryParams = new URLSearchParams({
          ticker: companyTicker
        });
        
        // Add date range if provided
        if (dateRange && dateRange.start && dateRange.end) {
          queryParams.append('startDate', dateRange.start);
          queryParams.append('endDate', dateRange.end);
        }
        
        await api.get(`/api/sec/fetch-filing-fix?${queryParams.toString()}`);

        // 3) Start async processing
        setReprocessStep('Starting AI processing...');
        const start = await api.post(`/api/sec/process-filings-async?ticker=${companyTicker}&dealId=${companyTicker}&enableEmbeddings=true`);
        const processingId = start.data?.processingId;
        if (!processingId) throw new Error('Failed to start processing');

        // 4) Poll status until completion (lightweight inline poller)
        setReprocessStep('Processing filings...');
        const startTime = Date.now();
        const maxMs = 30 * 60 * 1000; // 30 minutes
        while (Date.now() - startTime < maxMs) {
          const statusRes = await api.get(`/api/sec/processing-status/${processingId}`, { timeout: 120000 });
          const status = statusRes.data;
          if (status.status === 'completed') {
            setReprocessStep('Completed! You can ask questions now.');
            setIsReprocessing(false);
            return;
          }
          if (status.status === 'failed') {
            throw new Error(status.errors?.join(', ') || 'Processing failed');
          }
          await new Promise(r => setTimeout(r, 2000));
        }
        throw new Error('Processing timed out after 30 minutes');
      }
    } catch (err) {
      setIsReprocessing(false);
      setError(err.response?.data?.message || err.message || 'Failed to re-run processing');
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const SourcesDisplay = ({ sources }) => {
    const [showAllSources, setShowAllSources] = useState(false);
    
    if (!sources || sources.length === 0) return null;
    
    // Group sources by filing type and date to avoid duplicates
    const uniqueSources = sources.reduce((acc, source) => {
      const key = `${source.filingType}-${source.filingDate}`;
      if (!acc[key] || acc[key].confidence < source.confidence) {
        acc[key] = source;
      }
      return acc;
    }, {});
    
    const allSources = Object.values(uniqueSources);
    const displaySources = showAllSources ? allSources : allSources.slice(0, 6);
    
    return (
      <div style={{ 
        marginTop: '12px', 
        padding: '10px 14px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <strong style={{ color: '#495057', fontSize: '0.9rem', marginBottom: '8px', display: 'block' }}>
          📚 Sources:
        </strong>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {displaySources.map((source, index) => {
            const displayTitle = `${source.filingType} - ${source.section || 'Financial Data'}`;
            const displayDate = new Date(source.filingDate).toLocaleDateString();
            
            return (
              <div key={`${source.chunkId}-${index}`} style={{ 
                display: 'flex',
                alignItems: 'center',
                padding: '6px 8px',
                backgroundColor: 'white',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                fontSize: '0.8rem'
              }}>
                {/* Type Badge */}
                <span style={{ 
                  backgroundColor: source.type === 'text_chunk' ? '#28a745' : 
                                 source.type === 'xbrl_metric' ? '#007bff' : '#ffc107', 
                  color: source.type === 'xbrl_metric' ? 'white' : 
                         source.type === 'text_chunk' ? 'white' : '#212529',
                  padding: '2px 6px', 
                  borderRadius: '3px', 
                  fontSize: '0.7rem',
                  marginRight: '8px',
                  fontWeight: 'bold',
                  minWidth: '80px',
                  textAlign: 'center'
                }}>
                  {source.type === 'text_chunk' ? 'TEXT' : 
                   source.type === 'xbrl_metric' ? 'XBRL' : 'TABLE'}
                </span>
                
                {/* Filing Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {source.url ? (
                    <a 
                      href={source.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#007bff', 
                        textDecoration: 'none',
                        fontWeight: '500'
                      }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                      {displayTitle}
                    </a>
                  ) : (
                    <span style={{ fontWeight: '500', color: '#495057' }}>
                      {displayTitle}
                    </span>
                  )}
                  <div style={{ 
                    color: '#6c757d', 
                    fontSize: '0.75rem',
                    marginTop: '2px'
                  }}>
                    Filed: {displayDate}
                    {source.accessionNumber && (
                      <span style={{ marginLeft: '8px' }}>
                        • {source.accessionNumber}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Confidence Score */}
                <div style={{ 
                  marginLeft: '8px',
                  fontSize: '0.7rem',
                  color: '#6c757d'
                }}>
                  {Math.round(source.confidence * 100)}%
                </div>
              </div>
            );
          })}
          
          {allSources.length > displaySources.length && (
            <button
              onClick={() => setShowAllSources(!showAllSources)}
              style={{ 
                fontSize: '0.8rem', 
                color: '#007bff', 
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'center',
                marginTop: '4px',
                width: '100%',
                padding: '4px',
                borderRadius: '4px',
                textDecoration: 'underline'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              {showAllSources 
                ? '▲ Show fewer sources' 
                : `▼ Show ${allSources.length - displaySources.length} more sources`
              }
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div>
          <h2 className="chat-title">{companyName} ({companyTicker})</h2>
          {dateRange && (
            <p style={{ 
              fontSize: '0.9rem', 
              color: '#666', 
              margin: '4px 0 0 0',
              fontStyle: 'italic'
            }}>
              📅 Analysis period: {dateRange.start} to {dateRange.end}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="back-button" onClick={onBackToInput}>
            ← Back to Company Selection
          </button>
          <button 
            className="back-button" 
            onClick={reprocessTicker}
            disabled={isReprocessing}
            title="Re-download and re-process filings for this ticker"
          >
            {isReprocessing ? 'Re-processing...' : 'Re-run Processing'}
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="message assistant">
            <div className="message-content">
              Hello! I'm your AI financial analyst for {companyName}. 
              I can help you analyze their SEC filings, financial metrics, and answer questions about their business.
              <br /><br />
              Try asking me about:
              <ul style={{ marginTop: '8px', paddingLeft: '16px' }}>
                <li>Revenue and financial performance</li>
                <li>Risk factors and business challenges</li>
                <li>Recent developments and changes</li>
                <li>Specific financial metrics</li>
              </ul>
            </div>
            <div className="message-time">{formatTime(new Date())}</div>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`message ${message.role}`}>
            <div className="message-content">
              <ReactMarkdown 
                components={{
                  // Custom styling for markdown elements
                  h1: ({children}) => <h1 style={{fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0'}}>{children}</h1>,
                  h2: ({children}) => <h2 style={{fontSize: '1.3rem', fontWeight: 'bold', margin: '0.4rem 0'}}>{children}</h2>,
                  h3: ({children}) => <h3 style={{fontSize: '1.1rem', fontWeight: 'bold', margin: '0.3rem 0'}}>{children}</h3>,
                  p: ({children}) => <p style={{margin: '0.5rem 0', lineHeight: '1.5'}}>{children}</p>,
                  ul: ({children}) => <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>{children}</ul>,
                  ol: ({children}) => <ol style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>{children}</ol>,
                  li: ({children}) => <li style={{margin: '0.2rem 0'}}>{children}</li>,
                  strong: ({children}) => <strong style={{fontWeight: 'bold'}}>{children}</strong>,
                  em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>,
                  a: ({href, children}) => <a href={href} target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'underline'}}>{children}</a>,
                  code: ({children}) => <code style={{backgroundColor: '#f8f9fa', padding: '0.2rem 0.4rem', borderRadius: '3px', fontFamily: 'monospace'}}>{children}</code>,
                  blockquote: ({children}) => <blockquote style={{borderLeft: '4px solid #ddd', margin: '0.5rem 0', paddingLeft: '1rem', fontStyle: 'italic'}}>{children}</blockquote>
                }}
              >
                {message.content}
              </ReactMarkdown>
              {message.sources && <SourcesDisplay sources={message.sources} />}
            </div>
            <div className="message-time">{formatTime(message.timestamp)}</div>
          </div>
        ))}

        {isLoading && (
          <div className="message assistant">
            <div className="message-content">
              <div className="spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
              Analyzing your question...
            </div>
            <div className="message-time">{formatTime(new Date())}</div>
          </div>
        )}

        {isReprocessing && (
          <div className="message assistant">
            <div className="message-content">
              <div className="spinner" style={{ display: 'inline-block', marginRight: '8px' }}></div>
              {reprocessStep || 'Re-processing filings...'}
            </div>
            <div className="message-time">{formatTime(new Date())}</div>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <form onSubmit={handleSendMessage} className="chat-input-form">
          <input
            type="text"
            className="chat-input"
            placeholder={`Ask me anything about ${companyName}...`}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            disabled={isLoading}
          />
          <button 
            type="submit" 
            className="send-button"
            disabled={isLoading || !inputMessage.trim()}
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;
